
// @ts-ignore — xlsx-js-style has same API as xlsx but adds .s (style) support
import * as XLSX from "xlsx-js-style";

// ─── Color palette (matches your table UI) ────────────────────────────────────
const C = {
  HEADER_DARK:    "0F4C81",   // deep blue — main header bg
  HEADER_MID:     "1E293B",   // slate — secondary header / grand total bg
  QUARTER_BG:     "334155",   // quarter group header
  ANNUAL_BG:      "92400E",   // annual total group header
  GRAND_TOTAL_BG: "1E293B",   // grand total row bg
  WHITE_TEXT:     "FFFFFF",
  LIGHT_ROW:      "EEF2FF",   // quarter row fill
  ODD_ROW:        "F8FAFC",
  GROWTH_POS:     "059669",   // green
  GROWTH_NEG:     "DC2626",   // red
  GROWTH_ZERO:    "94A3B8",   // gray
  YELLOW_BG:      "FFFBEB",   // annual total cell
  YELLOW_LIGHT:   "FEF9C3",   // growth cell in annual
  DATA_TEXT:      "1E293B",
  BORDER_COLOR:   "CBD5E1",
};

// ─── Cell style factories ──────────────────────────────────────────────────────

const border = () => ({
  top:    { style: "thin", color: { rgb: C.BORDER_COLOR } },
  bottom: { style: "thin", color: { rgb: C.BORDER_COLOR } },
  left:   { style: "thin", color: { rgb: C.BORDER_COLOR } },
  right:  { style: "thin", color: { rgb: C.BORDER_COLOR } },
});



// xlsx-js-style accepts these horizontal alignment values
type HAlign = "left" | "center" | "right" | "fill" | "justify" | "centerContinuous" | "distributed";

/** Header cell style — for group / sub-column headers */
const headerStyle = (bgHex: string, bold = true, align: HAlign = "center") => ({
  font:      { bold, color: { rgb: C.WHITE_TEXT }, sz: 11 },
  fill:      { fgColor: { rgb: bgHex } },
  alignment: { horizontal: align, vertical: "center", wrapText: true },
  border:    border(),
});

/** Data cell style */
const dataStyle = (
  bgHex = "FFFFFF",
  bold  = false,
  align: HAlign = "right",
  color = C.DATA_TEXT
) => ({
  font:      { bold, color: { rgb: color }, sz: 10 },
  fill:      { fgColor: { rgb: bgHex } },
  alignment: { horizontal: align, vertical: "center" },
  border:    border(),
});

/** Grand total row cell style */
const grandTotalStyle = (align: HAlign = "right", growthColor?: string) => ({
  font:      { bold: true, color: { rgb: growthColor ?? C.WHITE_TEXT }, sz: 11 },
  fill:      { fgColor: { rgb: C.GRAND_TOTAL_BG } },
  alignment: { horizontal: align, vertical: "center" },
  border:    { ...border(), top: { style: "medium", color: { rgb: C.BORDER_COLOR } } },
});

// ─── Types ────────────────────────────────────────────────────────────────────

export type FilterMode = "all" | "monthly" | "quarterly";

export interface DashboardMRow {
  Particulars: string;
  UBold:       number;    // 1 = quarter bold row
  LYAmount:    number;
  CYAmount:    number;
  GrowthPer:   number;
}

export interface MonthRow {
  Month:     string;
  Bold:      number;
  LYAmount:  number;
  CYAmount:  number;
  GrowthPer: number;
}

export interface MatrixRow {
  Particular: string;
  DataM:      MonthRow[];
}

export interface TreeNodeFlat {
  particular: string;
  level:      number;
  dataM:      MonthRow[];
  expend:     number;
  nodeKey:    string;
}

export type ComparativeExportParams = {
  type:        "comparative";
  heading:     string;
  rows:        DashboardMRow[];
  yr1Label:    string;
  yr2Label:    string;
  filterMode:  FilterMode;
  grandTotal:  { lyTotal: number; cyTotal: number; growthPer: number };
};

export type MatrixExportParams = {
  type:       "matrix";
  heading:    string;
  rows:       MatrixRow[];
  yr1Label:   string;
  yr2Label:   string;
  filterMode: FilterMode;
};

export type ItemGroupExportParams = {
  type:       "itemgroup";
  heading:    string;
  rows:       TreeNodeFlat[];
  yr1Label:   string;
  yr2Label:   string;
  filterMode: FilterMode;
};
export type AccountGroupExportParams = {
  type:       "accountgroup";
  heading:    string;
  rows:       TreeNodeFlat[];
  yr1Label:   string;
  yr2Label:   string;
  filterMode: FilterMode;
};

export type ExportParams =
  | ComparativeExportParams
  | MatrixExportParams
  | ItemGroupExportParams
  | AccountGroupExportParams;

// ─── Static months ────────────────────────────────────────────────────────────

const STATIC_MONTHS = [
  { name: "April",     isQ: false, idx: 0  },
  { name: "May",       isQ: false, idx: 1  },
  { name: "June",      isQ: false, idx: 2  },
  { name: "Quarter 1", isQ: true,  idx: 3  },
  { name: "July",      isQ: false, idx: 4  },
  { name: "August",    isQ: false, idx: 5  },
  { name: "September", isQ: false, idx: 6  },
  { name: "Quarter 2", isQ: true,  idx: 7  },
  { name: "October",   isQ: false, idx: 8  },
  { name: "November",  isQ: false, idx: 9  },
  { name: "December",  isQ: false, idx: 10 },
  { name: "Quarter 3", isQ: true,  idx: 11 },
  { name: "January",   isQ: false, idx: 12 },
  { name: "February",  isQ: false, idx: 13 },
  { name: "March",     isQ: false, idx: 14 },
  { name: "Quarter 4", isQ: true,  idx: 15 },
];

const QUARTER_INDICES = new Set([3, 7, 11, 15]);

const getVisibleMonths = (filterMode: FilterMode) => {
  if (filterMode === "monthly")   return STATIC_MONTHS.filter((m) => !m.isQ);
  if (filterMode === "quarterly") return STATIC_MONTHS.filter((m) =>  m.isQ);
  return STATIC_MONTHS;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

// const fmtNum = (val: number) =>
//   Number(val || 0).toLocaleString("en-IN", {
//     minimumFractionDigits: 2,
//     maximumFractionDigits: 2,
//   });

// const fmtGrowth = (val: number) => {
//   const n = Number(val || 0);
//   return `${n > 0 ? "+" : ""}${n.toFixed(2)}%`;
// };

const growthColor = (val: number): string =>
  val > 0 ? C.GROWTH_POS : val < 0 ? C.GROWTH_NEG : C.GROWTH_ZERO;

/** Column index to Excel letter(s): 0→A, 25→Z, 26→AA … */
const colLetter = (n: number): string => {
  let s = "";
  n += 1;
  while (n > 0) {
    const rem = (n - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
};

// Excel number format strings
const NUM_FMT = "#,##0.00";
const PCT_FMT = '+#,##0.00"%";-#,##0.00"%";0.00"%"';

/** Set a STRING cell (labels, headers) */
const setCell = (ws: any, row: number, col: number, v: string | number, s: object) => {
  const addr = `${colLetter(col)}${row}`;
  ws[addr] = { v, t: typeof v === "number" ? "n" : "s", s };
  return addr;
};

/**
 * Set a NUMERIC cell — raw number so Excel can SUM/AVG.
 * Displayed with comma-separated 2-decimal format.
 */
const setNumCell = (ws: any, row: number, col: number, val: number, s: object) => {
  const addr = `${colLetter(col)}${row}`;
  ws[addr]   = { v: Number(val) || 0, t: "n", z: NUM_FMT, s };
  return addr;
};

/**
 * Set a GROWTH % cell — raw number, Excel formats with +/- sign.
 */
const setGrowthCell = (ws: any, row: number, col: number, val: number, s: object) => {
  const addr = `${colLetter(col)}${row}`;
  ws[addr]   = { v: Number(val) || 0, t: "n", z: PCT_FMT, s };
  return addr;
};

/** Merge cells helper */
const merge = (ws: any, r1: number, c1: number, r2: number, c2: number) => {
  if (!ws["!merges"]) ws["!merges"] = [];
  ws["!merges"].push({
    s: { r: r1 - 1, c: c1 },
    e: { r: r2 - 1, c: c2 },
  });
};

/** Set sheet range */
const setRange = (ws: any, maxRow: number, maxCol: number) => {
  ws["!ref"] = `A1:${colLetter(maxCol)}${maxRow}`;
};

/** Set column widths */
const setCols = (ws: any, widths: number[]) => {
  ws["!cols"] = widths.map((w) => ({ wch: w }));
};

/** Download the workbook */
const downloadWorkbook = (wb: any, filename: string) => {
  const now = new Date();
  const ts = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}`;
  const fname = `${filename.replace(/\s+/g, "_")}_${ts}.xlsx`;

  // Write to ArrayBuffer and trigger download via Blob
  const buf: ArrayBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a   = document.createElement("a");
  a.href    = url;
  a.download = fname;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 200);
};

// ══════════════════════════════════════════════════════════════════════════════
// BUILDER 1 — COMPARATIVE TABLE
// ══════════════════════════════════════════════════════════════════════════════
// Layout:
//   Row 1 : Title (merged)
//   Row 2 : Filter info
//   Row 3 : (blank)
//   Row 4 : Column headers  [Particulars | FY yr1 | FY yr2 | Growth %]
//   Row 5+ : Data rows
//   Last  : Grand Total row

const buildComparativeSheet = (p: ComparativeExportParams) => {
  const { heading, rows, yr1Label, yr2Label, grandTotal } = p;
  const ws: any = {};
  const COLS = 4; // Particulars, LY, CY, Growth
  let r = 1;

  // Row 1 — Title
  setCell(ws, r, 0, `${heading} Comparison Details`, headerStyle(C.HEADER_DARK, true, "left"));
  merge(ws, r, 0, r, COLS - 1);
  r++;

  // Row 2 — Filter
  setCell(ws, r, 0, `Filter: ${p.filterMode.toUpperCase()}`, {
    font: { italic: true, sz: 10, color: { rgb: "64748B" } },
    fill: { fgColor: { rgb: "F8FAFC" } },
  });
  merge(ws, r, 0, r, COLS - 1);
  r++;

  // Row 3 — blank
  r++;

  // Row 4 — Headers
  const headers = ["Particulars", `FY ${yr1Label}`, `FY ${yr2Label}`, "Growth %"];
  headers.forEach((h, c) => {
    setCell(ws, r, c, h, headerStyle(C.HEADER_DARK, true, c === 0 ? "left" : "center"));
  });
  r++;

  // Data rows
  rows.forEach((row) => {
    const isQ    = row.UBold === 1;
    const bg     = isQ ? C.LIGHT_ROW : "FFFFFF";
    const bold   = isQ;
    const gVal   = Number(row.GrowthPer || 0);
    const gColor = growthColor(gVal);

    setCell(ws, r, 0, (isQ ? "▶ " : "   ") + (row.Particulars || ""), dataStyle(bg, bold, "left"));
    setNumCell   (ws, r, 1, row.LYAmount  || 0, dataStyle(bg, bold, "right"));
    setNumCell   (ws, r, 2, row.CYAmount  || 0, dataStyle(bg, bold, "right"));
    setGrowthCell(ws, r, 3, gVal,               { ...dataStyle(bg, true, "right", gColor) });
    r++;
  });

  // Blank separator
  r++;

  // Grand Total row
  const gtGrowth = grandTotal.growthPer;
  setCell(ws, r, 0, "GRAND TOTAL",                 grandTotalStyle("left"));
  setNumCell   (ws, r, 1, grandTotal.lyTotal, grandTotalStyle("right"));
  setNumCell   (ws, r, 2, grandTotal.cyTotal, grandTotalStyle("right"));
  setGrowthCell(ws, r, 3, gtGrowth,
    grandTotalStyle("right", gtGrowth > 0 ? "86EFAC" : gtGrowth < 0 ? "FCA5A5" : "CBD5E1")
  );

  setRange(ws, r, COLS - 1);
  setCols(ws, [40, 18, 18, 14]);

  return ws;
};

// ══════════════════════════════════════════════════════════════════════════════
// BUILDER 2 — MATRIX TABLE  (Branch / Head / AccountCat / ItemCat)
// ══════════════════════════════════════════════════════════════════════════════
// Layout:
//   Row 1 : Title (merged across all cols)
//   Row 2 : Filter info
//   Row 3 : blank
//   Row 4 : Group headers   [Branch/Particular | April(3) | May(3) | … | Annual Total(3)]
//   Row 5 : Sub headers     [              ""  | LY|CY|Gr | …                           ]
//   Row 6+: Data rows
//   Last  : Grand Total

const buildMatrixSheet = (p: MatrixExportParams) => {
  const { heading, rows, yr1Label, yr2Label, filterMode } = p;
  const visibleMonths = getVisibleMonths(filterMode);
  const ws: any = {};

  // Total cols: 1 (particular) + months×3 + 3 (annual)
  const TOTAL_COLS = 1 + visibleMonths.length * 3 + 3;
  let r = 1;

  // Row 1 — Title
  setCell(ws, r, 0, heading, headerStyle(C.HEADER_DARK, true, "left"));
  merge(ws, r, 0, r, TOTAL_COLS - 1);
  r++;

  // Row 2 — Filter
  setCell(ws, r, 0, `Filter: ${filterMode.toUpperCase()}`, {
    font: { italic: true, sz: 10, color: { rgb: "64748B" } },
  });
  merge(ws, r, 0, r, TOTAL_COLS - 1);
  r++;

  // Row 3 — blank
  r++;

  // Row 4 — Group headers
  // Col 0: "Branch / Particular" spanning 2 rows (merge with row 5)
  setCell(ws, r, 0, "Branch / Particular", headerStyle(C.HEADER_DARK, true, "left"));
  merge(ws, r, 0, r + 1, 0); // span 2 rows

  let c = 1;
  visibleMonths.forEach((m) => {
    const bg = m.isQ ? C.QUARTER_BG : C.HEADER_DARK;
    setCell(ws, r, c, m.name, headerStyle(bg, true, "center"));
    merge(ws, r, c, r, c + 2); // span 3 cols
    // placeholder cells for merged area
    setCell(ws, r, c + 1, "", headerStyle(bg));
    setCell(ws, r, c + 2, "", headerStyle(bg));
    c += 3;
  });
  // Annual Total group header
  setCell(ws, r, c, "Annual Total", headerStyle(C.ANNUAL_BG, true, "center"));
  merge(ws, r, c, r, c + 2);
  setCell(ws, r, c + 1, "", headerStyle(C.ANNUAL_BG));
  setCell(ws, r, c + 2, "", headerStyle(C.ANNUAL_BG));
  r++;

  // Row 5 — Sub-column headers
  setCell(ws, r, 0, "", headerStyle(C.HEADER_DARK)); // placeholder for merged "Branch/Particular"
  c = 1;
  visibleMonths.forEach((m) => {
    const bg = m.isQ ? C.QUARTER_BG : C.HEADER_DARK;
    setCell(ws, r, c,     yr1Label,   headerStyle(bg, false, "center"));
    setCell(ws, r, c + 1, yr2Label,   headerStyle(bg, false, "center"));
    setCell(ws, r, c + 2, "Growth %", headerStyle(bg, false, "center"));
    c += 3;
  });
  setCell(ws, r, c,     yr1Label,   headerStyle(C.ANNUAL_BG, false, "center"));
  setCell(ws, r, c + 1, yr2Label,   headerStyle(C.ANNUAL_BG, false, "center"));
  setCell(ws, r, c + 2, "Growth %", headerStyle(C.ANNUAL_BG, false, "center"));
  r++;

  // Helper: write a data row (works for both normal rows and grand total)
  const writeDataRow = (row: MatrixRow, isGrandTotal = false) => {
    const bg = isGrandTotal ? C.GRAND_TOTAL_BG : "FFFFFF";
    const bold = isGrandTotal;

    if (isGrandTotal) {
      setCell(ws, r, 0, "GRAND TOTAL", grandTotalStyle("left"));
    } else {
      setCell(ws, r, 0, row.Particular, dataStyle(bg, bold, "left"));
    }

    c = 1;
    visibleMonths.forEach((m) => {
      const mData  = row.DataM?.[m.idx];
      const cellBg = isGrandTotal ? C.GRAND_TOTAL_BG : m.isQ ? "F1F5F9" : "FFFFFF";
      const gVal   = Number(mData?.GrowthPer || 0);
      const gColor = isGrandTotal
        ? (gVal > 0 ? "86EFAC" : gVal < 0 ? "FCA5A5" : "CBD5E1")
        : growthColor(gVal);

      setNumCell   (ws, r, c,     mData?.LYAmount  || 0, dataStyle(cellBg, bold, "right"));
      setNumCell   (ws, r, c + 1, mData?.CYAmount  || 0, dataStyle(cellBg, bold, "right"));
      setGrowthCell(ws, r, c + 2, gVal,
        isGrandTotal
          ? grandTotalStyle("right", gColor)
          : { ...dataStyle(m.isQ ? "E2E8F0" : "FFFFFF", true, "right", gColor) }
      );
      c += 3;
    });

    // Annual total
    let annLY = 0, annCY = 0;
    row.DataM?.forEach((md, idx) => {
      if (!QUARTER_INDICES.has(idx)) { annLY += md.LYAmount || 0; annCY += md.CYAmount || 0; }
    });
    const annGr = annLY !== 0 ? ((annCY - annLY) / Math.abs(annLY)) * 100 : annCY !== 0 ? 100 : 0;
    const annGrColor = isGrandTotal
      ? (annGr > 0 ? "86EFAC" : annGr < 0 ? "FCA5A5" : "CBD5E1")
      : growthColor(annGr);

    if (isGrandTotal) {
      setNumCell   (ws, r, c,     annLY, grandTotalStyle("right"));
      setNumCell   (ws, r, c + 1, annCY, grandTotalStyle("right"));
      setGrowthCell(ws, r, c + 2, annGr, grandTotalStyle("right", annGrColor));
    } else {
      setNumCell   (ws, r, c,     annLY, dataStyle(C.YELLOW_BG,    true, "right", "92400E"));
      setNumCell   (ws, r, c + 1, annCY, dataStyle(C.YELLOW_BG,    true, "right", "92400E"));
      setGrowthCell(ws, r, c + 2, annGr, dataStyle(C.YELLOW_LIGHT, true, "right", annGrColor));
    }
    r++;
  };

  // Write all data rows
  rows.forEach((row) => writeDataRow(row, false));

  // Grand Total
  const totalRow: MatrixRow = { Particular: "GRAND TOTAL", DataM: [] };
  STATIC_MONTHS.forEach((m) => {
    let tLY = 0, tCY = 0;
    rows.forEach((rw) => { tLY += rw.DataM?.[m.idx]?.LYAmount || 0; tCY += rw.DataM?.[m.idx]?.CYAmount || 0; });
    const tGr = tLY !== 0 ? ((tCY - tLY) / Math.abs(tLY)) * 100 : tCY !== 0 ? 100 : 0;
    totalRow.DataM.push({ Month: m.name, Bold: m.isQ ? 1 : 0, LYAmount: tLY, CYAmount: tCY, GrowthPer: tGr });
  });
  r++; // blank row before grand total
  writeDataRow(totalRow, true);

  setRange(ws, r, TOTAL_COLS - 1);

  // Column widths: col0=30, then 13,13,11 per month group
  const widths: number[] = [30];
  visibleMonths.forEach(() => widths.push(13, 13, 11));
  widths.push(14, 14, 11); // annual
  setCols(ws, widths);

  // Row heights for header rows
  ws["!rows"] = [
    { hpt: 22 }, // row1 title
    { hpt: 16 }, // row2 filter
    { hpt: 10 }, // row3 blank
    { hpt: 28 }, // row4 group headers
    { hpt: 20 }, // row5 sub headers
  ];

  return ws;
};



// ══════════════════════════════════════════════════════════════════════════════
// BUILDER 3 — ITEM GROUP WISE  (nested tree)
// ══════════════════════════════════════════════════════════════════════════════

const buildItemGroupSheet = (p: ItemGroupExportParams) => {
  const { heading, rows, yr1Label, yr2Label, filterMode } = p;
  const visibleMonths = getVisibleMonths(filterMode);
  const ws: any = {};

  // col 0 = Item Group, then months×3, then annual×3
  const TOTAL_COLS = 1 + visibleMonths.length * 3 + 3;
  let r = 1;

  // Row 1 — Title
  setCell(ws, r, 0, heading, headerStyle(C.HEADER_DARK, true, "left"));
  merge(ws, r, 0, r, TOTAL_COLS - 1);
  r++;

  // Row 2 — Filter
  setCell(ws, r, 0, `Filter: ${filterMode.toUpperCase()}`, {
    font: { italic: true, sz: 10, color: { rgb: "64748B" } },
  });
  merge(ws, r, 0, r, TOTAL_COLS - 1);
  r++;

  // Row 3 — blank
  r++;

  // Row 4 — Group headers
  setCell(ws, r, 0, "Item Group", headerStyle(C.HEADER_DARK, true, "left"));
  merge(ws, r, 0, r + 1, 0); // spans 2 rows

  let c = 1;
  visibleMonths.forEach((m) => {
    const bg = m.isQ ? C.QUARTER_BG : C.HEADER_DARK;
    setCell(ws, r, c,     m.name, headerStyle(bg, true, "center"));
    merge(ws, r, c, r, c + 2);
    setCell(ws, r, c + 1, "", headerStyle(bg));
    setCell(ws, r, c + 2, "", headerStyle(bg));
    c += 3;
  });
  setCell(ws, r, c,     "Annual Total", headerStyle(C.ANNUAL_BG, true, "center"));
  merge(ws, r, c, r, c + 2);
  setCell(ws, r, c + 1, "", headerStyle(C.ANNUAL_BG));
  setCell(ws, r, c + 2, "", headerStyle(C.ANNUAL_BG));
  r++;

  // Row 5 — Sub headers
  setCell(ws, r, 0, "", headerStyle(C.HEADER_DARK)); // placeholder for merged "Item Group"
  c = 1;
  visibleMonths.forEach((m) => {
    const bg = m.isQ ? C.QUARTER_BG : C.HEADER_DARK;
    setCell(ws, r, c,     yr1Label,   headerStyle(bg, false, "center"));
    setCell(ws, r, c + 1, yr2Label,   headerStyle(bg, false, "center"));
    setCell(ws, r, c + 2, "Growth %", headerStyle(bg, false, "center"));
    c += 3;
  });
  setCell(ws, r, c,     yr1Label,   headerStyle(C.ANNUAL_BG, false, "center"));
  setCell(ws, r, c + 1, yr2Label,   headerStyle(C.ANNUAL_BG, false, "center"));
  setCell(ws, r, c + 2, "Growth %", headerStyle(C.ANNUAL_BG, false, "center"));
  r++;

  // Data rows
  rows.forEach((node) => {
    const lvl    = Math.max(0, node.level);
    const isRoot = lvl === 0;
    const rowBg  = isRoot ? "F0F4FF" : lvl === 1 ? "FAFAFA" : "FFFFFF";
    const indent = "  ".repeat(lvl);

    // col 0 — Item Group name (indented by level)
    setCell(ws, r, 0, `${indent}${node.particular}`, dataStyle(rowBg, isRoot, "left"));

    c = 1;
    visibleMonths.forEach((m) => {
      const mData  = node.dataM?.[m.idx];
      const cellBg = m.isQ ? "F1F5F9" : rowBg;
      const gVal   = Number(mData?.GrowthPer || 0);
      const gColor = growthColor(gVal);

      setNumCell   (ws, r, c,     mData?.LYAmount || 0, dataStyle(cellBg, isRoot, "right"));
      setNumCell   (ws, r, c + 1, mData?.CYAmount || 0, dataStyle(cellBg, isRoot, "right"));
      setGrowthCell(ws, r, c + 2, gVal,
        { ...dataStyle(m.isQ ? "E2E8F0" : rowBg, true, "right", gColor) }
      );
      c += 3;
    });

    // Annual Total
    let annLY = 0, annCY = 0;
    node.dataM?.forEach((md, idx) => {
      if (!QUARTER_INDICES.has(idx)) { annLY += md.LYAmount || 0; annCY += md.CYAmount || 0; }
    });
    const annGr      = annLY !== 0 ? ((annCY - annLY) / Math.abs(annLY)) * 100 : annCY !== 0 ? 100 : 0;
    const annGrColor = growthColor(annGr);
    setNumCell   (ws, r, c,     annLY, dataStyle(C.YELLOW_BG,    isRoot, "right", "92400E"));
    setNumCell   (ws, r, c + 1, annCY, dataStyle(C.YELLOW_BG,    isRoot, "right", "92400E"));
    setGrowthCell(ws, r, c + 2, annGr, dataStyle(C.YELLOW_LIGHT, true,   "right", annGrColor));
    r++;
  });

  // Grand Total — sum only level-0 roots to avoid double-counting
  const rootNodes = rows.filter((n) => n.level === 0);
  r++; // blank row before grand total

  setCell(ws, r, 0, "GRAND TOTAL", grandTotalStyle("left"));

  c = 1;
  visibleMonths.forEach((m) => {
    let tLY = 0, tCY = 0;
    rootNodes.forEach((n) => {
      tLY += n.dataM?.[m.idx]?.LYAmount || 0;
      tCY += n.dataM?.[m.idx]?.CYAmount || 0;
    });
    const tGr    = tLY !== 0 ? ((tCY - tLY) / Math.abs(tLY)) * 100 : tCY !== 0 ? 100 : 0;
    const gColor = tGr > 0 ? "86EFAC" : tGr < 0 ? "FCA5A5" : "CBD5E1";

    setNumCell   (ws, r, c,     tLY, grandTotalStyle("right"));
    setNumCell   (ws, r, c + 1, tCY, grandTotalStyle("right"));
    setGrowthCell(ws, r, c + 2, tGr, grandTotalStyle("right", gColor));
    c += 3;
  });

  let gtAnnLY = 0, gtAnnCY = 0;
  rootNodes.forEach((n) => {
    n.dataM?.forEach((md, idx) => {
      if (!QUARTER_INDICES.has(idx)) { gtAnnLY += md.LYAmount || 0; gtAnnCY += md.CYAmount || 0; }
    });
  });
  const gtAnnGr      = gtAnnLY !== 0 ? ((gtAnnCY - gtAnnLY) / Math.abs(gtAnnLY)) * 100 : gtAnnCY !== 0 ? 100 : 0;
  const gtAnnGrColor = gtAnnGr > 0 ? "86EFAC" : gtAnnGr < 0 ? "FCA5A5" : "CBD5E1";
  setNumCell   (ws, r, c,     gtAnnLY, grandTotalStyle("right"));
  setNumCell   (ws, r, c + 1, gtAnnCY, grandTotalStyle("right"));
  setGrowthCell(ws, r, c + 2, gtAnnGr, grandTotalStyle("right", gtAnnGrColor));

  setRange(ws, r, TOTAL_COLS - 1);

  // col widths: col0=32 (name), then 13,13,11 per month, then 14,14,11 annual
  const widths: number[] = [32];
  visibleMonths.forEach(() => widths.push(13, 13, 11));
  widths.push(14, 14, 11);
  setCols(ws, widths);

  ws["!rows"] = [
    { hpt: 22 }, // row1 title
    { hpt: 16 }, // row2 filter
    { hpt: 10 }, // row3 blank
    { hpt: 28 }, // row4 group headers
    { hpt: 20 }, // row5 sub headers
  ];

  return ws;
};


// ══════════════════════════════════════════════════════════════════════════════
// BUILDER 4 — ACCOUNT GROUP WISE  (nested tree)
// ══════════════════════════════════════════════════════════════════════════════

const buildAccountGroupSheet = (p: AccountGroupExportParams) => {
  const { heading, rows, yr1Label, yr2Label, filterMode } = p;
  const visibleMonths = getVisibleMonths(filterMode);
  const ws: any = {};

  // col 0 = Account Group, then months×3, then annual×3
  const TOTAL_COLS = 1 + visibleMonths.length * 3 + 3;
  let r = 1;

  // Row 1 — Title
  setCell(ws, r, 0, heading, headerStyle(C.HEADER_DARK, true, "left"));
  merge(ws, r, 0, r, TOTAL_COLS - 1);
  r++;

  // Row 2 — Filter
  setCell(ws, r, 0, `Filter: ${filterMode.toUpperCase()}`, {
    font: { italic: true, sz: 10, color: { rgb: "64748B" } },
  });
  merge(ws, r, 0, r, TOTAL_COLS - 1);
  r++;

  // Row 3 — blank
  r++;

  // Row 4 — Group headers
  setCell(ws, r, 0, "Account Group", headerStyle(C.HEADER_DARK, true, "left"));
  merge(ws, r, 0, r + 1, 0); // spans 2 rows

  let c = 1;
  visibleMonths.forEach((m) => {
    const bg = m.isQ ? C.QUARTER_BG : C.HEADER_DARK;
    setCell(ws, r, c,     m.name, headerStyle(bg, true, "center"));
    merge(ws, r, c, r, c + 2);
    setCell(ws, r, c + 1, "", headerStyle(bg));
    setCell(ws, r, c + 2, "", headerStyle(bg));
    c += 3;
  });
  setCell(ws, r, c,     "Annual Total", headerStyle(C.ANNUAL_BG, true, "center"));
  merge(ws, r, c, r, c + 2);
  setCell(ws, r, c + 1, "", headerStyle(C.ANNUAL_BG));
  setCell(ws, r, c + 2, "", headerStyle(C.ANNUAL_BG));
  r++;

  // Row 5 — Sub headers
  setCell(ws, r, 0, "", headerStyle(C.HEADER_DARK)); // placeholder for merged "Account Group"
  c = 1;
  visibleMonths.forEach((m) => {
    const bg = m.isQ ? C.QUARTER_BG : C.HEADER_DARK;
    setCell(ws, r, c,     yr1Label,   headerStyle(bg, false, "center"));
    setCell(ws, r, c + 1, yr2Label,   headerStyle(bg, false, "center"));
    setCell(ws, r, c + 2, "Growth %", headerStyle(bg, false, "center"));
    c += 3;
  });
  setCell(ws, r, c,     yr1Label,   headerStyle(C.ANNUAL_BG, false, "center"));
  setCell(ws, r, c + 1, yr2Label,   headerStyle(C.ANNUAL_BG, false, "center"));
  setCell(ws, r, c + 2, "Growth %", headerStyle(C.ANNUAL_BG, false, "center"));
  r++;

  // Data rows
  rows.forEach((node) => {
    const lvl    = Math.max(0, node.level);
    const isRoot = lvl === 0;
    const rowBg  = isRoot ? "F0F4FF" : lvl === 1 ? "FAFAFA" : "FFFFFF";
    const indent = "  ".repeat(lvl);

    // col 0 — Account Group name (indented by level)
    setCell(ws, r, 0, `${indent}${node.particular}`, dataStyle(rowBg, isRoot, "left"));

    c = 1;
    visibleMonths.forEach((m) => {
      const mData  = node.dataM?.[m.idx];
      const cellBg = m.isQ ? "F1F5F9" : rowBg;
      const gVal   = Number(mData?.GrowthPer || 0);
      const gColor = growthColor(gVal);

      setNumCell   (ws, r, c,     mData?.LYAmount || 0, dataStyle(cellBg, isRoot, "right"));
      setNumCell   (ws, r, c + 1, mData?.CYAmount || 0, dataStyle(cellBg, isRoot, "right"));
      setGrowthCell(ws, r, c + 2, gVal,
        { ...dataStyle(m.isQ ? "E2E8F0" : rowBg, true, "right", gColor) }
      );
      c += 3;
    });

    // Annual Total
    let annLY = 0, annCY = 0;
    node.dataM?.forEach((md, idx) => {
      if (!QUARTER_INDICES.has(idx)) { annLY += md.LYAmount || 0; annCY += md.CYAmount || 0; }
    });
    const annGr      = annLY !== 0 ? ((annCY - annLY) / Math.abs(annLY)) * 100 : annCY !== 0 ? 100 : 0;
    const annGrColor = growthColor(annGr);
    setNumCell   (ws, r, c,     annLY, dataStyle(C.YELLOW_BG,    isRoot, "right", "92400E"));
    setNumCell   (ws, r, c + 1, annCY, dataStyle(C.YELLOW_BG,    isRoot, "right", "92400E"));
    setGrowthCell(ws, r, c + 2, annGr, dataStyle(C.YELLOW_LIGHT, true,   "right", annGrColor));
    r++;
  });

  // Grand Total — sum only level-0 roots to avoid double-counting
  const rootNodes = rows.filter((n) => n.level === 0);
  r++; // blank row before grand total

  setCell(ws, r, 0, "GRAND TOTAL", grandTotalStyle("left"));

  c = 1;
  visibleMonths.forEach((m) => {
    let tLY = 0, tCY = 0;
    rootNodes.forEach((n) => {
      tLY += n.dataM?.[m.idx]?.LYAmount || 0;
      tCY += n.dataM?.[m.idx]?.CYAmount || 0;
    });
    const tGr    = tLY !== 0 ? ((tCY - tLY) / Math.abs(tLY)) * 100 : tCY !== 0 ? 100 : 0;
    const gColor = tGr > 0 ? "86EFAC" : tGr < 0 ? "FCA5A5" : "CBD5E1";

    setNumCell   (ws, r, c,     tLY, grandTotalStyle("right"));
    setNumCell   (ws, r, c + 1, tCY, grandTotalStyle("right"));
    setGrowthCell(ws, r, c + 2, tGr, grandTotalStyle("right", gColor));
    c += 3;
  });

  let gtAnnLY = 0, gtAnnCY = 0;
  rootNodes.forEach((n) => {
    n.dataM?.forEach((md, idx) => {
      if (!QUARTER_INDICES.has(idx)) { gtAnnLY += md.LYAmount || 0; gtAnnCY += md.CYAmount || 0; }
    });
  });
  const gtAnnGr      = gtAnnLY !== 0 ? ((gtAnnCY - gtAnnLY) / Math.abs(gtAnnLY)) * 100 : gtAnnCY !== 0 ? 100 : 0;
  const gtAnnGrColor = gtAnnGr > 0 ? "86EFAC" : gtAnnGr < 0 ? "FCA5A5" : "CBD5E1";
  setNumCell   (ws, r, c,     gtAnnLY, grandTotalStyle("right"));
  setNumCell   (ws, r, c + 1, gtAnnCY, grandTotalStyle("right"));
  setGrowthCell(ws, r, c + 2, gtAnnGr, grandTotalStyle("right", gtAnnGrColor));

  setRange(ws, r, TOTAL_COLS - 1);

  // col widths: col0=32 (name), then 13,13,11 per month, then 14,14,11 annual
  const widths: number[] = [32];
  visibleMonths.forEach(() => widths.push(13, 13, 11));
  widths.push(14, 14, 11);
  setCols(ws, widths);

  ws["!rows"] = [
    { hpt: 22 }, // row1 title
    { hpt: 16 }, // row2 filter
    { hpt: 10 }, // row3 blank
    { hpt: 28 }, // row4 group headers
    { hpt: 20 }, // row5 sub headers
  ];

  return ws;
};



// ─── Public API ──────────────────────────────────────────────────────────────

export const exportToExcel = (params: ExportParams): void => {
  const wb = XLSX.utils.book_new();
  let ws: any;
  let sheetName = "";
  let filename  = "";

  switch (params.type) {
    case "comparative":
      ws        = buildComparativeSheet(params);
      sheetName = "Comparative";
      filename  = `${params.heading}_${params.filterMode}`;
      break;

    case "matrix":
      ws        = buildMatrixSheet(params);
      sheetName = params.heading.substring(0, 31); // Excel 31 char limit
      filename  = `${params.heading}_${params.filterMode}`;
      break;

    case "itemgroup":
      ws        = buildItemGroupSheet(params);
      sheetName = "Item Group Wise";
      filename  = `${params.heading}_${params.filterMode}`;
      break;

    case "accountgroup":
      ws        = buildAccountGroupSheet(params);
      sheetName = "Account Group Wise";
      filename  = `${params.heading}_${params.filterMode}`;
      break;

    default:
      console.error("exportToExcel: unknown type");
      return;
  }

  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  downloadWorkbook(wb, filename);
};